import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../../components/layout/AppLayout';
import { useCourseApi } from '../../../../api/courses/courses';
import { TLesson } from '../../../../models/courses/lesson';
import { normalizeGraphicDesignLessonConfig,
  TGraphicDesignLessonConfig } from '../../../../models/courses/graphicDesign';
import GraphicDesignLessonAdmin from '../../../../components/GraphicDesignLesson/admin/GraphicDesignLessonAdmin';
import GraphicDesignLessonTeacher from '../../../../components/GraphicDesignLesson/teacher/GraphicDesignLessonTeacher';

const LessonGraphicDesign: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getLessonDetailsById, saveLessonDetails } = useCourseApi();
  const stateLesson: TLesson | undefined = location.state?.lesson;
  const teacherMode: boolean = location.state?.teacherMode || false;
  const epic = location.state?.epic;
  const [editingLesson, setEditingLesson] = useState<TLesson | null>(stateLesson ?? null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [importedStatement, setImportedStatement] = useState('');
  const lessonIdFromUrl = useMemo(() => {
    const lessonId = new URLSearchParams(location.search).get('lessonId');
    return lessonId ? parseInt(lessonId, 10) : undefined;
  }, [location.search]);
  useEffect(() => {
    const lessonId = lessonIdFromUrl ?? stateLesson?.id;
    if (!lessonId) {
      message.warning(t('graphicDesignLesson.page.selectLesson'));
      navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      return;
    }
    setLoading(true);
    getLessonDetailsById(lessonId).then(response => {
      const lessonData = response.data;
      lessonData.config = {
        id: lessonData.config?.id ?? 0,
        lesson_id: lessonData.id,
        config: normalizeGraphicDesignLessonConfig(lessonData.config?.config),
      };
      setEditingLesson(lessonData);
      setImportedStatement(lessonData.statement || '');
    }).catch(error => {
      message.error(error.response?.data ?? error.message);
      navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const currentConfig = useMemo(() => normalizeGraphicDesignLessonConfig(
    editingLesson?.config?.config), [editingLesson]);
  const handleConfigChange = (config: TGraphicDesignLessonConfig) => {
    setDirty(true);
    setEditingLesson(prev => prev ? { ...prev, config: {
      id: prev.config?.id ?? 0, lesson_id: prev.id, config,
    } } : prev);
  };
  const handleSave = async () => {
    if (!editingLesson) return;
    setSaving(true);
    try {
      const statement = importedStatement || editingLesson.statement || currentConfig.statement || '';
      await saveLessonDetails({ ...editingLesson, statement, config: {
        id: editingLesson.config?.id ?? 0, lesson_id: editingLesson.id,
        config: { ...currentConfig, statement },
      } });
      message.success(t('graphicDesignLesson.page.saveSuccess'));
      setDirty(false);
    } catch (error: any) {
      message.error(error.response?.data ?? error.message ?? error);
    } finally {
      setSaving(false);
    }
  };
  return <AppLayout title={editingLesson?.name || t('graphicDesignLesson.page.title')}
    onBackNavigation={() => navigate(-1)} showBackButton loading={loading}>
    {editingLesson && (teacherMode
      ? <GraphicDesignLessonTeacher config={currentConfig}
        statement={editingLesson.statement || currentConfig.statement} />
      : <GraphicDesignLessonAdmin config={currentConfig}
      onConfigChange={handleConfigChange} onSave={handleSave} saving={saving} dirty={dirty}
      initialStatement={importedStatement} onStatementChange={value => {
        setImportedStatement(value); setDirty(true);
      }}
      readOnly={teacherMode} />)}
  </AppLayout>;
};

export default LessonGraphicDesign;
